import React from 'react';
import {
  AppBar,
  AppBarProps,
  Box,
  Link,
  Toolbar,
  alpha,
  styled,
} from '@mui/material';
import { Link as LinkRouter } from 'react-router-dom';
import { getPolyglot } from '../../../../i18n';
import logoFooterSrc from './logo-footer.svg';

const LinkStyled: typeof Link = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textDecoration: 'underline',
  '&:hover': {
    color: alpha(theme.palette.primary.contrastText, 0.8),
  },
})) as typeof Link;
export enum ALLOWED_URLS {
  ABOUT_US = 'about_us',
  STATUS = 'status',
  TERMS_AND_CONDITIONS = 'terms_and_conditions',
  PRIVACY_POLICY = 'privacy_policy',
  COOKIE_POLICY = 'cookie_policy',
  IMPRESSUM = 'impressum',
}
export interface LinkItem {
  label: string;
  to: string;
  external: boolean;
}
export interface LinksFooterProps extends AppBarProps {
  logoFooter?: string;
  urlAboutUs?: string;
  slots?: { LinkComponent: typeof Link };
  logoHeight?: string | number;
  urlStatus?: string;
  predefinedLinks?: Partial<Record<ALLOWED_URLS, LinkItem>>;
}
function LinksFooter(props: LinksFooterProps) {
  const {
    logoFooter = logoFooterSrc,
    urlAboutUs = 'http://stoerk-tronic.com/',
    urlStatus = 'https://stats.uptimerobot.com/xxuCT7SMXT',
    predefinedLinks = null,
    ...appBarProps
  } = props;
  const LinkComponent = props.slots?.LinkComponent ?? LinkStyled;
  const polyglot = getPolyglot();
  const links: Record<ALLOWED_URLS, LinkItem> = predefinedLinks
    ? (predefinedLinks as Record<ALLOWED_URLS, LinkItem>)
    : {
        [ALLOWED_URLS.TERMS_AND_CONDITIONS]: {
          label: polyglot.t('footer.terms_and_conditions'),
          to: '/terms-and-conditions',
          external: false,
        },
        [ALLOWED_URLS.PRIVACY_POLICY]: {
          label: polyglot.t('footer.privacy_policy'),
          to: '/privacy',
          external: false,
        },
        [ALLOWED_URLS.COOKIE_POLICY]: {
          label: polyglot.t('footer.cookie_policy'),
          to: '/cookies',
          external: false,
        },
        [ALLOWED_URLS.IMPRESSUM]: {
          label: polyglot.t('footer.impressum'),
          to: '/impressum',
          external: false,
        },
        [ALLOWED_URLS.ABOUT_US]: {
          label: polyglot.t('footer.about_us'),
          to: urlAboutUs,
          external: true,
        },
        [ALLOWED_URLS.STATUS]: {
          label: polyglot.t('footer.status'),
          to: urlStatus,
          external: true,
        },
      };

  return (
    <AppBar
      position="static"
      component={'footer'}
      color="primary"
      sx={{
        alignItems: 'center',
        display: { xs: 'none', md: 'flex' },
      }}
      {...appBarProps}
    >
      <Toolbar
        sx={{
          height: '4rem',
          maxWidth: 1536,
          justifyContent: 'space-between',
          mx: 2,
          gap: 2,
          width: '100%',
          margin: 'auto',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box
          sx={{
            gap: { lg: '4rem', md: '2rem' },
            display: 'flex',
          }}
        >
          {Object.values(links).map((link) =>
            link.external ? (
              <LinkComponent key={link.to} href={link.to} target="_blank">
                {link.label}
              </LinkComponent>
            ) : (
              <LinkComponent key={link.to} to={link.to} component={LinkRouter}>
                {link.label}
              </LinkComponent>
            )
          )}
        </Box>

        {!!logoFooter && (
          <img
            src={logoFooter}
            height={props.logoHeight ?? 30}
            alt="logo-stoerk"
          />
        )}
      </Toolbar>
    </AppBar>
  );
}

export default LinksFooter;
